import { z } from 'zod';
import {
    IpxFlowFieldBindingSource,
    IpxDomRpaWorkflowStorageVersion,
    IpxManualCaptureTriggerKind,
    IpxWebResourceElementKind,
    IpxWebResourceElementKindSupportedActions,
    IpxWebResourceModelDocumentStorageVersion,
    validateIpxWebResourceModel,
} from '../samba-rpa-workflow.types';
import { IpxCredentialFieldNamePattern } from '../secret.types';
import type {
    GenericUxKeyModifier,
    GenericUxSequenceInit,
    GenericUxSequenceStep,
    IpxCredentialFlowFieldBinding,
    IpxDomRpaWorkflow,
    RpaDomElementAction,
    IpxFlowElementAbsentOutcomePredicate,
    IpxFlowElementPresentOutcomePredicate,
    IpxFlowMappedTextContainsOutcomePredicate,
    IpxFlowMappedTextEqualsOutcomePredicate,
    IpxFlowOutcomePredicate,
    IpxFlowOutcomePredicateNormalizer,
    IpxFlowScreenReachedOutcomePredicate,
    IpxFlowUrlPatternPrerequisite,
    IpxFlowUrlPrerequisite,
    IpxDomRpaWorkflowStep,
    IpxDomRpaWorkflowStepForKind,
    IpxDomRpaWorkflowV1,
    IpxDomRpaWorkflowV2,
    IpxManualCaptureTriggerPolicy,
    IpxWebResourceDomElementDescriptor,
    IpxWebResourceDomElementDescriptorV2,
    IpxWebResourceDomElementMapV2,
    IpxWebResourceElementLocator,
    IpxWebResourceDomElementDescriptorV1,
    IpxWebResourceDomElementMap,
    IpxWebResourceDomElementMapV1,
    IpxWebResourceElementEvidence,
    IpxWebResourceElementEvidenceV1,
    IpxWebResourceModelDocument,
    IpxWebResourceModelDocumentV1,
    IpxWebResourceModelDocumentV2,
    IpxWebResourceModelDocumentV3,
    IpxWebResourceScreenMarkerKey,
    IpxWebResourceScreenMarkerMap,
    RpaCredentialFieldSelector,
    RpaCredentialFieldSelectorMap,
    RpaDomAttributeScreenDiscriminantExpression,
    RpaDomSelector,
    RpaDomSelectorFieldMap,
    RpaDomTextScreenDiscriminantExpression,
    RpaSanitizedDomSnapshot,
    RpaSanitizedDomStableAttributeMap,
    RpaScreenDefinition,
    RpaScreenDetectionPredicate,
    RpaScreenDetectionProfile,
    RpaScreenDiscriminantExpression,
    RpaScreenKey,
    StoredIpxDomRpaWorkflow,
    StoredIpxWebResourceModelDocument,
} from '../samba-rpa-workflow.types';
import { UrlPatternStringSchema } from '../backend_config.types';
import { NonEmptyStringSchema, PositiveIntSchema } from './common.schemas';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../stored-json-number';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';

export const GenericUxFramePathSchema: z.ZodType<number[]> = z.array(z.number().int().min(0));

export const GenericUxTimeoutMsSchema: z.ZodType<number> = PositiveIntSchema;

export const GenericUxDelayMsSchema: z.ZodType<number> = z.number().int().min(0);

export const RpaDomSelectorSchema: z.ZodType<RpaDomSelector> = z.object({
    selector: NonEmptyStringSchema,
    innerText: z.string().optional(),
    textContent: z.string().optional(),
    framePath: GenericUxFramePathSchema.optional(),
});

export const RpaDomSelectorFieldMapSchema: z.ZodType<RpaDomSelectorFieldMap> = z.record(
    NonEmptyStringSchema,
    RpaDomSelectorSchema
);

export const RpaScreenKeySchema: z.ZodType<RpaScreenKey> = NonEmptyStringSchema;

export const RpaDomTextScreenDiscriminantExpressionSchema: z.ZodType<RpaDomTextScreenDiscriminantExpression> =
    z.object({
        kind: z.literal('dom_text'),
        selector: RpaDomSelectorSchema,
        normalizer: z.literal('visible_text_v1'),
    });

export const RpaDomAttributeScreenDiscriminantExpressionSchema: z.ZodType<RpaDomAttributeScreenDiscriminantExpression> =
    z.object({
        kind: z.literal('dom_attribute'),
        selector: RpaDomSelectorSchema,
        attribute: NonEmptyStringSchema,
        normalizer: z.literal('trim_v1'),
    });

export const RpaScreenDiscriminantExpressionSchema: z.ZodType<RpaScreenDiscriminantExpression> =
    z.union([
        RpaDomTextScreenDiscriminantExpressionSchema,
        RpaDomAttributeScreenDiscriminantExpressionSchema,
    ]);

export const RpaScreenDetectionPredicateSchema: z.ZodType<RpaScreenDetectionPredicate> = z.object({
    expression: RpaScreenDiscriminantExpressionSchema,
    expected_value: NonEmptyStringSchema,
});

export const RpaScreenDefinitionSchema: z.ZodType<RpaScreenDefinition> = z.object({
    screen_key: RpaScreenKeySchema,
    display_name: NonEmptyStringSchema,
    detection_predicate: RpaScreenDetectionPredicateSchema.optional(),
});

export const RpaScreenDetectionProfileSchema: z.ZodType<RpaScreenDetectionProfile> = z
    .object({
        screens: z.record(RpaScreenKeySchema, RpaScreenDefinitionSchema),
    })
    .superRefine((profile, ctx) => {
        for (const screenKey of Object.keys(profile.screens)) {
            const definition = profile.screens[screenKey];
            if (definition === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Screen definition is missing',
                    path: ['screens', screenKey],
                });
                continue;
            }

            if (definition.screen_key !== screenKey) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Screen definition key must match map key',
                    path: ['screens', screenKey, 'screen_key'],
                });
            }
        }
    });

export const RpaScreenDiscriminantProfileSchema: z.ZodType<RpaScreenDetectionProfile> =
    RpaScreenDetectionProfileSchema;

export const RpaSanitizedDomStableAttributeMapSchema: z.ZodType<RpaSanitizedDomStableAttributeMap> =
    z.record(NonEmptyStringSchema, z.string());

export const RpaSanitizedDomSnapshotSchema: z.ZodType<RpaSanitizedDomSnapshot> = z.lazy(() =>
    z.object({
        tag_name: NonEmptyStringSchema,
        stable_attributes: RpaSanitizedDomStableAttributeMapSchema,
        visible_text: z.string().optional(),
        children: z.array(RpaSanitizedDomSnapshotSchema),
    })
);

export const RpaCredentialFieldSelectorSchema: z.ZodType<RpaCredentialFieldSelector> = z.object({
    selector: NonEmptyStringSchema,
    innerText: z.string().optional(),
    required_screen_key: RpaScreenKeySchema.optional(),
});

export const RpaCredentialFieldSelectorMapSchema: z.ZodType<RpaCredentialFieldSelectorMap> =
    z.record(NonEmptyStringSchema, RpaCredentialFieldSelectorSchema);

export const RpaDomElementActionSchema: z.ZodType<RpaDomElementAction> = z.union([
    z.literal('wait'),
    z.literal('read'),
    z.literal('click'),
    z.literal('right_click'),
    z.literal('double_click'),
    z.literal('scroll_to_bottom'),
    z.literal('set_text'),
    z.literal('paste_text'),
    z.literal('type_text'),
    z.literal('keypress'),
]);

export const IpxWebResourceElementKindSchema: z.ZodType<IpxWebResourceElementKind> =
    z.enum(IpxWebResourceElementKind);

export const IpxWebResourceElementEvidenceSchema: z.ZodType<IpxWebResourceElementEvidence> = z
    .object({
        stable_text: z.array(NonEmptyStringSchema).min(1).optional(),
        stable_attributes: RpaSanitizedDomStableAttributeMapSchema.optional(),
    })
    .strict()
    .superRefine((evidence, ctx) => {
        if (evidence.stable_text === undefined && evidence.stable_attributes === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Mapped element evidence must contain stable text or stable attributes',
            });
        }
    });

export const IpxWebResourceElementEvidenceV1Schema: z.ZodType<IpxWebResourceElementEvidenceV1> = z
    .object({
        stable_text: z
            .union([NonEmptyStringSchema, z.array(NonEmptyStringSchema).min(1)])
            .optional(),
        stable_attributes: RpaSanitizedDomStableAttributeMapSchema.optional(),
    })
    .strict()
    .superRefine((evidence, ctx) => {
        if (evidence.stable_text === undefined && evidence.stable_attributes === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Mapped element evidence must contain stable text or stable attributes',
            });
        }
    });

export const IpxWebResourceScreenMarkerKeySchema: z.ZodType<IpxWebResourceScreenMarkerKey> =
    NonEmptyStringSchema;

export const IpxWebResourceScreenMarkerMapSchema: z.ZodType<IpxWebResourceScreenMarkerMap> =
    z.record(IpxWebResourceScreenMarkerKeySchema, RpaDomSelectorSchema);

export const IpxFlowFieldBindingSourceSchema: z.ZodType<IpxFlowFieldBindingSource> =
    z.enum(IpxFlowFieldBindingSource);

export const IpxCredentialFlowFieldNameSchema: z.ZodType<string> = z
    .string()
    .min(1)
    .max(64)
    .regex(IpxCredentialFieldNamePattern);

export const IpxCredentialFlowFieldBindingSchema: z.ZodType<IpxCredentialFlowFieldBinding> = z
    .object({
        credential_field_name: IpxCredentialFlowFieldNameSchema,
        source: IpxFlowFieldBindingSourceSchema,
        element_key: NonEmptyStringSchema,
    })
    .strict();

export const IpxScreenMarkerDomRpaWorkflowStepSchema: z.ZodType<
    IpxDomRpaWorkflowStepForKind<'SCREEN_MARKER'>
> = z
    .object({
        kind: z.literal(IpxWebResourceElementKind.SCREEN_MARKER),
        element_key: NonEmptyStringSchema,
        action: z.enum(IpxWebResourceElementKindSupportedActions.SCREEN_MARKER),
    })
    .strict();

export const IpxTextDomRpaWorkflowStepSchema: z.ZodType<IpxDomRpaWorkflowStepForKind<'TEXT'>> = z
    .object({
        kind: z.literal(IpxWebResourceElementKind.TEXT),
        element_key: NonEmptyStringSchema,
        action: z.enum(IpxWebResourceElementKindSupportedActions.TEXT),
    })
    .strict();

export const IpxFieldDomRpaWorkflowStepSchema: z.ZodType<IpxDomRpaWorkflowStepForKind<'FIELD'>> = z
    .object({
        kind: z.literal(IpxWebResourceElementKind.FIELD),
        element_key: NonEmptyStringSchema,
        action: z.enum(IpxWebResourceElementKindSupportedActions.FIELD),
    })
    .strict();

export const IpxSecretDomRpaWorkflowStepSchema: z.ZodType<IpxDomRpaWorkflowStepForKind<'SECRET'>> =
    z
        .object({
            kind: z.literal(IpxWebResourceElementKind.SECRET),
            element_key: NonEmptyStringSchema,
            action: z.enum(IpxWebResourceElementKindSupportedActions.SECRET),
        })
        .strict();

export const IpxButtonDomRpaWorkflowStepSchema: z.ZodType<IpxDomRpaWorkflowStepForKind<'BUTTON'>> =
    z
        .object({
            kind: z.literal(IpxWebResourceElementKind.BUTTON),
            element_key: NonEmptyStringSchema,
            action: z.enum(IpxWebResourceElementKindSupportedActions.BUTTON),
        })
        .strict();

export const IpxDomRpaWorkflowStepSchema: z.ZodType<IpxDomRpaWorkflowStep> = z.union([
    IpxScreenMarkerDomRpaWorkflowStepSchema,
    IpxTextDomRpaWorkflowStepSchema,
    IpxFieldDomRpaWorkflowStepSchema,
    IpxSecretDomRpaWorkflowStepSchema,
    IpxButtonDomRpaWorkflowStepSchema,
]);

export const IpxDomRpaWorkflowStepListSchema: z.ZodType<readonly IpxDomRpaWorkflowStep[]> = z.array(
    IpxDomRpaWorkflowStepSchema
);

export const IpxDomRpaWorkflowStorageVersionSchema: z.ZodType<IpxDomRpaWorkflowStorageVersion> =
    z.union([z.literal(1), z.literal(2)]);

const IpxDomRpaWorkflowStorageVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'ipx-dom-rpa-workflow.workflow_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(IpxDomRpaWorkflowStorageVersion),
    },
});

export const IpxDomRpaWorkflowV1Schema: z.ZodType<IpxDomRpaWorkflowV1> =
    IpxDomRpaWorkflowStepListSchema;

export const IpxDomRpaWorkflowV2Schema: z.ZodType<IpxDomRpaWorkflowV2> = z
    .object({
        workflow_version: z.literal(2),
        steps: IpxDomRpaWorkflowStepListSchema,
    })
    .strict();

export const StoredIpxDomRpaWorkflowV2Schema: z.ZodType<IpxDomRpaWorkflowV2> = z.preprocess(
    (value: unknown): unknown =>
        normalizeStoredJsonSafeIntegerObjectFields(value, {
            safeIntegerDomain: IpxDomRpaWorkflowStorageVersionSafeIntegerDomain,
            fields: ['workflow_version'],
        }),
    IpxDomRpaWorkflowV2Schema
);

export const StoredIpxDomRpaWorkflowSchema: z.ZodType<StoredIpxDomRpaWorkflow> = z.union([
    StoredIpxDomRpaWorkflowV2Schema,
    IpxDomRpaWorkflowV1Schema,
]);

export function normalizeIpxDomRpaWorkflowV1(workflow: IpxDomRpaWorkflowV1): IpxDomRpaWorkflow {
    return workflow;
}

export function normalizeIpxDomRpaWorkflowV2(workflow: IpxDomRpaWorkflowV2): IpxDomRpaWorkflow {
    return workflow.steps;
}

export const IpxDomRpaWorkflowStorageContract = defineStoredJsonContract({
    contractName: 'IpxDomRpaWorkflow',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'IpxDomRpaWorkflowV1Schema',
            schema: IpxDomRpaWorkflowV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['workflow_version'],
                },
            ],
            normalize: normalizeIpxDomRpaWorkflowV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredIpxDomRpaWorkflowV2Schema',
            schema: StoredIpxDomRpaWorkflowV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['workflow_version'],
                    markerValue: 2,
                },
            ],
            normalize: normalizeIpxDomRpaWorkflowV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredIpxDomRpaWorkflowParser: z.ZodType<IpxDomRpaWorkflow> =
    buildStoredJsonValueParser(IpxDomRpaWorkflowStorageContract);

export function serializeIpxDomRpaWorkflowV2(workflow: IpxDomRpaWorkflow): IpxDomRpaWorkflowV2 {
    return {
        workflow_version: 2,
        steps: IpxDomRpaWorkflowStepListSchema.parse(workflow),
    };
}

export const IpxFlowOutcomePredicateNormalizerSchema: z.ZodType<IpxFlowOutcomePredicateNormalizer> =
    z.literal('trim_v1');

export const IpxFlowScreenReachedOutcomePredicateSchema: z.ZodType<IpxFlowScreenReachedOutcomePredicate> =
    z
        .object({
            kind: z.literal('marker_context_reached'),
            required_screen_marker_keys: z.array(IpxWebResourceScreenMarkerKeySchema).min(1),
        })
        .strict();

export const IpxFlowMappedTextEqualsOutcomePredicateSchema: z.ZodType<IpxFlowMappedTextEqualsOutcomePredicate> =
    z
        .object({
            kind: z.literal('mapped_text_equals'),
            element_key: NonEmptyStringSchema,
            expected_value: NonEmptyStringSchema,
            normalizer: IpxFlowOutcomePredicateNormalizerSchema,
        })
        .strict();

export const IpxFlowMappedTextContainsOutcomePredicateSchema: z.ZodType<IpxFlowMappedTextContainsOutcomePredicate> =
    z
        .object({
            kind: z.literal('mapped_text_contains'),
            element_key: NonEmptyStringSchema,
            expected_value: NonEmptyStringSchema,
            normalizer: IpxFlowOutcomePredicateNormalizerSchema,
        })
        .strict();

export const IpxFlowElementPresentOutcomePredicateSchema: z.ZodType<IpxFlowElementPresentOutcomePredicate> =
    z
        .object({
            kind: z.literal('element_present'),
            element_key: NonEmptyStringSchema,
        })
        .strict();

export const IpxFlowElementAbsentOutcomePredicateSchema: z.ZodType<IpxFlowElementAbsentOutcomePredicate> =
    z
        .object({
            kind: z.literal('element_absent'),
            element_key: NonEmptyStringSchema,
        })
        .strict();

export const IpxFlowOutcomePredicateSchema: z.ZodType<IpxFlowOutcomePredicate> = z.union([
    IpxFlowScreenReachedOutcomePredicateSchema,
    IpxFlowMappedTextEqualsOutcomePredicateSchema,
    IpxFlowMappedTextContainsOutcomePredicateSchema,
    IpxFlowElementPresentOutcomePredicateSchema,
    IpxFlowElementAbsentOutcomePredicateSchema,
]);

export const IpxFlowUrlPatternPrerequisiteSchema: z.ZodType<IpxFlowUrlPatternPrerequisite> = z
    .object({
        kind: z.literal('url_pattern'),
        url_pattern: UrlPatternStringSchema,
    })
    .strict();

export const IpxFlowUrlPrerequisiteSchema: z.ZodType<IpxFlowUrlPrerequisite> =
    IpxFlowUrlPatternPrerequisiteSchema;

export const IpxManualCaptureTriggerKindSchema: z.ZodType<IpxManualCaptureTriggerKind> = z.enum(
    IpxManualCaptureTriggerKind
);

export const IpxManualCaptureTriggerPolicySchema: z.ZodType<IpxManualCaptureTriggerPolicy> = z
    .object({
        trigger_kinds: z.array(IpxManualCaptureTriggerKindSchema).min(1),
    })
    .strict()
    .superRefine((policy, ctx) => {
        const seenTriggerKinds = new Set<IpxManualCaptureTriggerKind>();
        for (const [triggerIndex, triggerKind] of policy.trigger_kinds.entries()) {
            if (seenTriggerKinds.has(triggerKind)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'manual capture trigger kinds must be unique',
                    path: ['trigger_kinds', triggerIndex],
                });
            }
            seenTriggerKinds.add(triggerKind);
        }
    });

export const IpxWebResourceElementLocatorSchema: z.ZodType<IpxWebResourceElementLocator> = z
    .object({
        role: z.string().min(1).max(64),
        label: z.string().min(1).max(256),
    })
    .strict();

export const IpxWebResourceDomElementDescriptorSchema: z.ZodType<IpxWebResourceDomElementDescriptor> =
    z
        .object({
            element_key: NonEmptyStringSchema,
            display_name: NonEmptyStringSchema,
            selector: RpaDomSelectorSchema,
            element_kind: IpxWebResourceElementKindSchema,
            required_screen_marker_keys: z.array(IpxWebResourceScreenMarkerKeySchema).min(1),
            evidence: IpxWebResourceElementEvidenceSchema.optional(),
            notes: NonEmptyStringSchema.optional(),
            locator: IpxWebResourceElementLocatorSchema.optional(),
            frame_url_pattern: z.string().min(1).max(256).startsWith('/').optional(),
        })
        .strict()
        .superRefine((element, ctx) => {
            const seenMarkerKeys = new Set<string>();
            for (const [markerIndex, markerKey] of element.required_screen_marker_keys.entries()) {
                if (seenMarkerKeys.has(markerKey)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'DOM element screen marker keys must be unique',
                        path: ['required_screen_marker_keys', markerIndex],
                    });
                }
                seenMarkerKeys.add(markerKey);
            }
        });

export const IpxWebResourceDomElementDescriptorV2Schema: z.ZodType<IpxWebResourceDomElementDescriptorV2> =
    z
        .object({
            element_key: NonEmptyStringSchema,
            display_name: NonEmptyStringSchema,
            selector: RpaDomSelectorSchema,
            element_kind: IpxWebResourceElementKindSchema,
            required_screen_marker_keys: z.array(IpxWebResourceScreenMarkerKeySchema).min(1),
            evidence: IpxWebResourceElementEvidenceSchema.optional(),
            notes: NonEmptyStringSchema.optional(),
        })
        .strict()
        .superRefine((element, ctx) => {
            const seenMarkerKeys = new Set<string>();
            for (const [markerIndex, markerKey] of element.required_screen_marker_keys.entries()) {
                if (seenMarkerKeys.has(markerKey)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'DOM element screen marker keys must be unique',
                        path: ['required_screen_marker_keys', markerIndex],
                    });
                }
                seenMarkerKeys.add(markerKey);
            }
        });

export const IpxWebResourceDomElementDescriptorV1Schema: z.ZodType<IpxWebResourceDomElementDescriptorV1> =
    z
        .object({
            element_key: NonEmptyStringSchema,
            display_name: NonEmptyStringSchema,
            selector: RpaDomSelectorSchema,
            element_kind: IpxWebResourceElementKindSchema,
            required_screen_marker_keys: z.array(IpxWebResourceScreenMarkerKeySchema).min(1),
            evidence: IpxWebResourceElementEvidenceV1Schema.optional(),
            notes: NonEmptyStringSchema.optional(),
        })
        .strict()
        .superRefine((element, ctx) => {
            const seenMarkerKeys = new Set<string>();
            for (const [markerIndex, markerKey] of element.required_screen_marker_keys.entries()) {
                if (seenMarkerKeys.has(markerKey)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'DOM element screen marker keys must be unique',
                        path: ['required_screen_marker_keys', markerIndex],
                    });
                }
                seenMarkerKeys.add(markerKey);
            }
        });

export const IpxWebResourceDomElementMapSchema: z.ZodType<IpxWebResourceDomElementMap> = z
    .record(NonEmptyStringSchema, IpxWebResourceDomElementDescriptorSchema)
    .superRefine((domElementMap, ctx) => {
        for (const elementKey of Object.keys(domElementMap)) {
            const element = domElementMap[elementKey];
            if (element === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'DOM element descriptor is missing',
                    path: [elementKey],
                });
                continue;
            }
            if (element.element_key !== elementKey) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'DOM element descriptor key must match map key',
                    path: [elementKey, 'element_key'],
                });
            }
        }
    });

export const IpxWebResourceDomElementMapV2Schema: z.ZodType<IpxWebResourceDomElementMapV2> = z
    .record(NonEmptyStringSchema, IpxWebResourceDomElementDescriptorV2Schema)
    .superRefine((domElementMap, ctx) => {
        for (const elementKey of Object.keys(domElementMap)) {
            const element = domElementMap[elementKey];
            if (element === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'DOM element descriptor is missing',
                    path: [elementKey],
                });
                continue;
            }
            if (element.element_key !== elementKey) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'DOM element descriptor key must match map key',
                    path: [elementKey, 'element_key'],
                });
            }
        }
    });

export const IpxWebResourceDomElementMapV1Schema: z.ZodType<IpxWebResourceDomElementMapV1> = z
    .record(NonEmptyStringSchema, IpxWebResourceDomElementDescriptorV1Schema)
    .superRefine((domElementMap, ctx) => {
        for (const elementKey of Object.keys(domElementMap)) {
            const element = domElementMap[elementKey];
            if (element === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'DOM element descriptor is missing',
                    path: [elementKey],
                });
                continue;
            }
            if (element.element_key !== elementKey) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'DOM element descriptor key must match map key',
                    path: [elementKey, 'element_key'],
                });
            }
        }
    });

export const IpxWebResourceModelDocumentSchema: z.ZodType<IpxWebResourceModelDocument> = z
    .object({
        screen_markers: IpxWebResourceScreenMarkerMapSchema,
        dom_elements: IpxWebResourceDomElementMapSchema,
    })
    .strip()
    .superRefine((document, ctx) => {
        for (const issue of validateIpxWebResourceModel(document).issues) {
            ctx.addIssue({
                code: 'custom',
                message: issue.message,
                path: [...issue.path],
            });
        }
    });

export const IpxWebResourceModelDocumentStorageVersionSchema: z.ZodType<IpxWebResourceModelDocumentStorageVersion> =
    z.union([z.literal(1), z.literal(2), z.literal(3)]);

const IpxWebResourceModelDocumentStorageVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'ipx-web-resource-model-document.model_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(IpxWebResourceModelDocumentStorageVersion),
    },
});

export const IpxWebResourceModelDocumentV1Schema: z.ZodType<IpxWebResourceModelDocumentV1> = z
    .object({
        model_version: z.literal(1).optional(),
        screen_markers: IpxWebResourceScreenMarkerMapSchema,
        dom_elements: IpxWebResourceDomElementMapV1Schema,
    })
    .strip();

export const IpxWebResourceModelDocumentV2Schema: z.ZodType<IpxWebResourceModelDocumentV2> = z
    .object({
        model_version: z.literal(2),
        screen_markers: IpxWebResourceScreenMarkerMapSchema,
        dom_elements: IpxWebResourceDomElementMapV2Schema,
    })
    .strip()
    .superRefine((document, ctx) => {
        for (const issue of validateIpxWebResourceModel(document).issues) {
            ctx.addIssue({
                code: 'custom',
                message: issue.message,
                path: [...issue.path],
            });
        }
    });

export const IpxWebResourceModelDocumentV3Schema: z.ZodType<IpxWebResourceModelDocumentV3> = z
    .object({
        model_version: z.literal(3),
        screen_markers: IpxWebResourceScreenMarkerMapSchema,
        dom_elements: IpxWebResourceDomElementMapSchema,
    })
    .strip()
    .superRefine((document, ctx) => {
        for (const issue of validateIpxWebResourceModel(document).issues) {
            ctx.addIssue({
                code: 'custom',
                message: issue.message,
                path: [...issue.path],
            });
        }
    });

export const StoredIpxWebResourceModelDocumentV1Schema: z.ZodType<IpxWebResourceModelDocumentV1> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: IpxWebResourceModelDocumentStorageVersionSafeIntegerDomain,
                fields: ['model_version'],
            }),
        IpxWebResourceModelDocumentV1Schema
    );

export const StoredIpxWebResourceModelDocumentV2Schema: z.ZodType<IpxWebResourceModelDocumentV2> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: IpxWebResourceModelDocumentStorageVersionSafeIntegerDomain,
                fields: ['model_version'],
            }),
        IpxWebResourceModelDocumentV2Schema
    );

export const StoredIpxWebResourceModelDocumentV3Schema: z.ZodType<IpxWebResourceModelDocumentV3> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: IpxWebResourceModelDocumentStorageVersionSafeIntegerDomain,
                fields: ['model_version'],
            }),
        IpxWebResourceModelDocumentV3Schema
    );

export const StoredIpxWebResourceModelDocumentSchema: z.ZodType<StoredIpxWebResourceModelDocument> =
    z.union([
        StoredIpxWebResourceModelDocumentV3Schema,
        StoredIpxWebResourceModelDocumentV2Schema,
        StoredIpxWebResourceModelDocumentV1Schema,
    ]);

function normalizeIpxWebResourceElementEvidenceV1(
    evidence: IpxWebResourceElementEvidenceV1
): IpxWebResourceElementEvidence {
    const stableTextResult =
        evidence.stable_text === undefined
            ? undefined
            : NonEmptyStringSchema.safeParse(evidence.stable_text);
    const stableText =
        evidence.stable_text === undefined
            ? undefined
            : stableTextResult?.success === true
              ? [stableTextResult.data]
              : z.array(NonEmptyStringSchema).min(1).parse(evidence.stable_text);
    return {
        ...(stableText === undefined ? {} : { stable_text: stableText }),
        ...(evidence.stable_attributes === undefined
            ? {}
            : { stable_attributes: evidence.stable_attributes }),
    };
}

function normalizeIpxWebResourceDomElementDescriptorV1(
    descriptor: IpxWebResourceDomElementDescriptorV1
): IpxWebResourceDomElementDescriptor {
    return {
        element_key: descriptor.element_key,
        display_name: descriptor.display_name,
        selector: descriptor.selector,
        element_kind: descriptor.element_kind,
        required_screen_marker_keys: descriptor.required_screen_marker_keys,
        ...(descriptor.evidence === undefined
            ? {}
            : {
                  evidence: normalizeIpxWebResourceElementEvidenceV1(descriptor.evidence),
              }),
        ...(descriptor.notes === undefined ? {} : { notes: descriptor.notes }),
    };
}

export function normalizeIpxWebResourceModelDocumentV1(
    document: IpxWebResourceModelDocumentV1
): IpxWebResourceModelDocument {
    const domElements: IpxWebResourceDomElementMap = {};
    for (const elementKey of Object.keys(document.dom_elements)) {
        const descriptor = document.dom_elements[elementKey];
        if (descriptor === undefined) {
            continue;
        }
        domElements[elementKey] = normalizeIpxWebResourceDomElementDescriptorV1(descriptor);
    }
    return {
        screen_markers: document.screen_markers,
        dom_elements: domElements,
    };
}

export function normalizeIpxWebResourceModelDocumentV2(
    document: IpxWebResourceModelDocumentV2
): IpxWebResourceModelDocument {
    return {
        screen_markers: document.screen_markers,
        dom_elements: document.dom_elements,
    };
}

export function normalizeIpxWebResourceModelDocumentV3(
    document: IpxWebResourceModelDocumentV3
): IpxWebResourceModelDocument {
    return {
        screen_markers: document.screen_markers,
        dom_elements: document.dom_elements,
    };
}

export function normalizeParsedIpxWebResourceModelDocument(
    document: IpxWebResourceModelDocument
): IpxWebResourceModelDocument {
    return document;
}

function addNestedZodIssues(
    ctx: z.core.ParsePayload<unknown>,
    issues: readonly z.core.$ZodIssue[]
): void {
    for (const issue of issues) {
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            message: issue.message,
            path: [...issue.path],
        });
    }
}

export const IpxWebResourceModelDocumentV1Parser: z.ZodType<IpxWebResourceModelDocument> =
    StoredIpxWebResourceModelDocumentV1Schema.transform((document, ctx) => {
        const parseResult = IpxWebResourceModelDocumentSchema.safeParse(
            normalizeIpxWebResourceModelDocumentV1(document)
        );
        if (!parseResult.success) {
            addNestedZodIssues(ctx, parseResult.error.issues);
            return z.NEVER;
        }
        return parseResult.data;
    });

export const IpxWebResourceModelDocumentV2Parser: z.ZodType<IpxWebResourceModelDocument> =
    StoredIpxWebResourceModelDocumentV2Schema.transform((document, ctx) => {
        const parseResult = IpxWebResourceModelDocumentSchema.safeParse(
            normalizeIpxWebResourceModelDocumentV2(document)
        );
        if (!parseResult.success) {
            addNestedZodIssues(ctx, parseResult.error.issues);
            return z.NEVER;
        }
        return parseResult.data;
    });

export const IpxWebResourceModelDocumentV3Parser: z.ZodType<IpxWebResourceModelDocument> =
    StoredIpxWebResourceModelDocumentV3Schema.transform((document, ctx) => {
        const parseResult = IpxWebResourceModelDocumentSchema.safeParse(
            normalizeIpxWebResourceModelDocumentV3(document)
        );
        if (!parseResult.success) {
            addNestedZodIssues(ctx, parseResult.error.issues);
            return z.NEVER;
        }
        return parseResult.data;
    });

export const IpxWebResourceModelDocumentVersionMarkerSchema: z.ZodType<{
    readonly model_version?: unknown;
}> = z
    .object({
        model_version: z.unknown().optional(),
    })
    .passthrough();

export const IpxWebResourceModelDocumentStorageContract = defineStoredJsonContract({
    contractName: 'IpxWebResourceModelDocument',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'IpxWebResourceModelDocumentV1Parser',
            schema: IpxWebResourceModelDocumentV1Parser,
            guards: [
                {
                    kind: 'json_marker_absent_or_equals',
                    markerPath: ['model_version'],
                    markerValue: 1,
                },
            ],
            normalize: normalizeParsedIpxWebResourceModelDocument,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'IpxWebResourceModelDocumentV2Parser',
            schema: IpxWebResourceModelDocumentV2Parser,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['model_version'],
                    markerValue: 2,
                },
            ],
            normalize: normalizeParsedIpxWebResourceModelDocument,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v3: {
            schemaName: 'IpxWebResourceModelDocumentV3Parser',
            schema: IpxWebResourceModelDocumentV3Parser,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['model_version'],
                    markerValue: 3,
                },
            ],
            normalize: normalizeParsedIpxWebResourceModelDocument,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v3',
});

export const StoredIpxWebResourceModelDocumentParser: z.ZodType<IpxWebResourceModelDocument> =
    buildStoredJsonValueParser(IpxWebResourceModelDocumentStorageContract);

export function serializeIpxWebResourceModelDocumentV3(
    document: IpxWebResourceModelDocument
): IpxWebResourceModelDocumentV3 {
    const currentDocument = IpxWebResourceModelDocumentSchema.parse(document);
    return {
        model_version: 3,
        ...currentDocument,
    };
}

export const GenericUxKeyModifierSchema: z.ZodType<GenericUxKeyModifier> = z.union([
    z.literal('Alt'),
    z.literal('Control'),
    z.literal('Meta'),
    z.literal('Shift'),
]);

export const GenericUxSequenceStepSchema: z.ZodType<GenericUxSequenceStep> = z
    .object({
        action: RpaDomElementActionSchema,
        selector: NonEmptyStringSchema.optional(),
        framePath: GenericUxFramePathSchema.optional(),
        timeoutMs: GenericUxTimeoutMsSchema.optional(),
        text: z.string().optional(),
        delayMs: GenericUxDelayMsSchema.optional(),
        key: NonEmptyStringSchema.optional(),
        modifiers: z.array(GenericUxKeyModifierSchema).optional(),
        milliseconds: PositiveIntSchema.optional(),
    })
    .strict()
    .superRefine((step, ctx) => {
        switch (step.action) {
            case 'read':
            case 'click':
            case 'right_click':
            case 'double_click':
            case 'scroll_to_bottom':
            case 'set_text':
            case 'paste_text':
            case 'type_text':
                if (step.selector === undefined) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Generic UX DOM operation steps require selector',
                        path: ['selector'],
                    });
                }
                break;
            case 'wait':
            case 'keypress':
                break;
        }
        switch (step.action) {
            case 'set_text':
            case 'paste_text':
            case 'type_text':
                if (step.text === undefined) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Generic UX text operation steps require text',
                        path: ['text'],
                    });
                }
                break;
            case 'read':
            case 'click':
            case 'right_click':
            case 'double_click':
            case 'scroll_to_bottom':
            case 'wait':
            case 'keypress':
                break;
        }
        if (step.action === 'keypress' && step.key === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Generic UX keypress steps require key',
                path: ['key'],
            });
        }
        if (step.action === 'wait' && step.milliseconds === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'Generic UX wait steps require milliseconds',
                path: ['milliseconds'],
            });
        }
    });

export const GenericUxSequenceInitSchema: z.ZodType<GenericUxSequenceInit> = z.object({
    tabId: PositiveIntSchema,
    steps: z.array(GenericUxSequenceStepSchema).min(1),
});
